import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { usePageView } from '../hooks/usePageView';

const Help = ({ user, setUser }) => {
  usePageView('help');
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'getting-started',
      title: '🚀 Getting Started',
      keywords: 'getting started login sign in google authentication dashboard navigation welcome',
      content: (
        <div>
          <h4>Welcome to Baad.tv!</h4>
          <p>Your personal TV show tracking and social review platform. Join the flock! 🐑</p>
          <ol>
            <li><strong>Sign In:</strong> Click "Continue with Google" on the landing page to authenticate with your Google account</li>
            <li><strong>Dashboard:</strong> After login, you'll see your dashboard with statistics and recent shows</li>
            <li><strong>Navigation:</strong> Use the top navigation bar to access different sections:
              <ul>
                <li>Dashboard - Overview of your show statistics</li>
                <li>My BaadTV (My Shows) - Manage all your tracked shows</li>
                <li>Graze - AI-powered show recommendations</li>
                <li>Feed the Flock - See reviews from friends</li>
                <li>Find Your Flock (Friends) - Find and follow other users</li>
                <li>Help - Access this help guide</li>
                <li>About - Learn about Baad.tv</li>
              </ul>
            </li>
          </ol>
        </div>
      )
    },
    {
      id: 'graze',
      title: '✨ Graze - AI Recommendations',
      keywords: 'graze recommendations ai discover suggestions flock picks streaming availability personalized',
      content: (
        <div>
          <h4>Discover Your Next Binge</h4>
          <p>Graze uses AI to analyse your viewing history and suggest shows you'll love!</p>
          
          <p><strong>How It Works:</strong></p>
          <ol>
            <li>Our AI analyses your tracked shows, ratings, and reviews</li>
            <li>It identifies patterns in your viewing preferences</li>
            <li>It factors in highly-rated shows from your flock (friends)</li>
            <li>You get personalised recommendations with explanations</li>
          </ol>

          <p><strong>Recommendation Types:</strong></p>
          <ul>
            <li><strong>🐑 Flock Picks:</strong> Shows your friends loved - includes their average rating</li>
            <li><strong>💎 Hidden Gems:</strong> Lesser-known shows that match your taste</li>
            <li><strong>❤️ Similar to Favorites:</strong> Shows like ones you've rated highly</li>
            <li><strong>📊 Genre Match:</strong> New shows in genres you enjoy</li>
          </ul>

          <p><strong>Streaming Availability:</strong></p>
          <ul>
            <li>Select your country from the dropdown (16+ countries supported)</li>
            <li>Green checkmark = Available on streaming services in your country</li>
            <li>Orange pin = May not be available in your country</li>
            <li>Shows aren't excluded just because they're unavailable - they're just flagged</li>
          </ul>

          <p><strong>Adding Recommendations:</strong></p>
          <ul>
            <li>Click "Add to Watchlist" to instantly add a show to your collection</li>
            <li>Shows are added with "Want to Watch" status</li>
            <li>Click "Refresh" to get fresh recommendations</li>
          </ul>

          <p><strong>Tips for Better Recommendations:</strong></p>
          <ul>
            <li>Rate more shows - the AI learns from your ratings</li>
            <li>Follow friends - their ratings influence "Flock Picks"</li>
            <li>Write reviews - helps the AI understand your preferences better</li>
          </ul>
        </div>
      )
    },
    {
      id: 'adding-shows',
      title: '📺 Adding & Managing Shows',
      keywords: 'adding shows managing edit delete create new status watching waiting completed want to watch streaming service',
      content: (
        <div>
          <h4>How to Track Shows</h4>
          <p><strong>Adding a New Show:</strong></p>
          <ol>
            <li>Click the "+ Add Show" button on Dashboard or My Shows page</li>
            <li>Enter the show title (required)</li>
            <li>Click "UPDATE SHOW DETAILS" to automatically fetch artwork, genre, and year</li>
            <li>Choose a status:
              <ul>
                <li><strong>Currently Watching</strong> - Shows you're actively watching</li>
                <li><strong>Waiting for Next Season</strong> - Shows on hiatus</li>
                <li><strong>Completed</strong> - Shows you've finished</li>
                <li><strong>Want to Watch</strong> - Shows on your watchlist</li>
              </ul>
            </li>
            <li>Select streaming service (preset or add custom)</li>
            <li>Add your rating (1-10) and review (optional)</li>
            <li>Choose whether to share your review to the feed</li>
            <li>Click "Add Show"</li>
          </ol>

          <p><strong>Editing Shows:</strong></p>
          <ul>
            <li>Go to My Shows page</li>
            <li>Click "Edit" on any show card</li>
            <li>Update any information and click "Update Show"</li>
          </ul>

          <p><strong>Deleting Shows:</strong></p>
          <ul>
            <li>Click "Delete" on any show card</li>
            <li>Confirm the deletion</li>
          </ul>
        </div>
      )
    },
    {
      id: 'tour',
      title: '🗺️ Product Tour',
      keywords: 'tour guide walkthrough help introduction getting started',
      content: (
        <div>
          <h4>Take the Interactive Tour</h4>
          <p>New to Baad.tv? Take our interactive tour to learn about all the features!</p>
          
          <p><strong>Starting the Tour:</strong></p>
          <ul>
            <li>First-time users: The tour starts automatically after login</li>
            <li>Returning users: Click your profile icon → "Take a Tour" to restart anytime</li>
          </ul>

          <p><strong>What You'll Learn:</strong></p>
          <ol>
            <li>Your Paddock - Dashboard overview and statistics</li>
            <li>My Baad.tv - Managing your show collection</li>
            <li>Graze - AI-powered recommendations</li>
            <li>My Feed - Social reviews from friends</li>
            <li>The Flock - Finding and following friends</li>
            <li>Help Screen - Getting support when needed</li>
          </ol>

          <p>The tour takes about a minute and helps you get the most out of Baad.tv!</p>
        </div>
      )
    },
    {
      id: 'posters',
      title: '🖼️ Updating Show Details & Images',
      keywords: 'posters images pictures artwork tmdb search update poster background details genre year metadata episode tracking',
      content: (
        <div>
          <h4>Automatic Show Details Updates</h4>
          <p>Baad.tv uses TMDB (The Movie Database) to automatically fetch official show information:</p>
          
          <p><strong>Update Show Details Button:</strong></p>
          <ul>
            <li>Click "UPDATE SHOW DETAILS" to fetch all show metadata with one click:
              <ul>
                <li>Portrait poster image</li>
                <li>Widescreen backdrop/background image</li>
                <li>Genre tags (Drama, Comedy, etc.)</li>
                <li>Release year</li>
                <li><strong>Episode tracking data</strong> - enables status badges like "New Episode", "Ended", etc.</li>
              </ul>
            </li>
            <li>The app finds the best match from TMDB instantly</li>
            <li>Works in both Add Show and Edit Show screens</li>
          </ul>

          <p><strong>Tips for Best Results:</strong></p>
          <ul>
            <li>Use the exact show title (e.g., "Breaking Bad" not "BB")</li>
            <li>Check the year to identify the correct show</li>
            <li>If no results appear, try alternative spellings</li>
          </ul>

          <p><strong>Custom Poster URL:</strong></p>
          <ul>
            <li>Click "Use Custom URL" if you have your own poster image</li>
            <li>Paste the image URL and click "Use This Image"</li>
            <li>The poster will appear on your Dashboard and My Shows page</li>
          </ul>

          <p><strong>No Images Required:</strong></p>
          <ul>
            <li>Images are optional - you can skip this step</li>
            <li>Shows without images will display title and information only</li>
          </ul>
        </div>
      )
    },
    {
      id: 'episode-badges',
      title: '🆕 Episode Status Badges',
      keywords: 'episode badges status new episode season ended canceled returning air date release schedule notifications',
      content: (
        <div>
          <h4>Track Episode Availability</h4>
          <p>Baad.tv automatically tracks episode status for your shows using TMDB data. Status badges appear on your show cards:</p>
          
          <p><strong>Badge Types:</strong></p>
          <ul>
            <li><span style={{background: '#22c55e', color: '#000', padding: '2px 8px', borderRadius: '4px', fontWeight: 600}}>NEW EPISODE</span> - A new episode is now available to watch</li>
            <li><span style={{background: '#3b82f6', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontWeight: 600}}>In 3 days</span> - Next episode coming soon (within 7 days)</li>
            <li><span style={{background: '#6366f1', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontWeight: 600}}>Mar 15</span> - Next episode scheduled for a specific date</li>
            <li><span style={{background: '#f59e0b', color: '#000', padding: '2px 8px', borderRadius: '4px', fontWeight: 600}}>NEW SEASON COMING</span> - Show is returning with a new season</li>
            <li><span style={{background: '#f97316', color: '#000', padding: '2px 8px', borderRadius: '4px', fontWeight: 600}}>AWAITING RENEWAL</span> - Waiting for network to renew</li>
            <li><span style={{background: '#6b7280', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontWeight: 600}}>ENDED</span> - Show has concluded</li>
            <li><span style={{background: '#ef4444', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontWeight: 600}}>CANCELED</span> - Show was canceled</li>
          </ul>

          <p><strong>Notifications:</strong></p>
          <ul>
            <li>You'll receive a notification when a new episode becomes available</li>
            <li>Check the bell icon for episode notifications</li>
            <li>Notifications include the episode number (e.g., S05E08)</li>
          </ul>

          <p><strong>How It Works:</strong></p>
          <ul>
            <li>Episode data refreshes automatically when you view My Shows</li>
            <li>Data updates once per day to avoid excessive API calls</li>
            <li>Shows added via TMDB search automatically get episode tracking</li>
          </ul>

          <p><strong>⚠️ Important: Release Date Regions</strong></p>
          <p>Air dates shown are from TMDB and typically reflect the <strong>original broadcast region</strong> (usually USA). Here's what this means for New Zealand viewers:</p>
          
          <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem', marginBottom: '1rem'}}>
            <thead>
              <tr style={{borderBottom: '1px solid #333'}}>
                <th style={{textAlign: 'left', padding: '0.5rem', color: '#fff'}}>Platform</th>
                <th style={{textAlign: 'left', padding: '0.5rem', color: '#fff'}}>NZ Release</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{borderBottom: '1px solid #222'}}>
                <td style={{padding: '0.5rem'}}>🟢 Netflix Originals</td>
                <td style={{padding: '0.5rem'}}>Same day (global release)</td>
              </tr>
              <tr style={{borderBottom: '1px solid #222'}}>
                <td style={{padding: '0.5rem'}}>🟢 Disney+ Originals</td>
                <td style={{padding: '0.5rem'}}>Same day (global release)</td>
              </tr>
              <tr style={{borderBottom: '1px solid #222'}}>
                <td style={{padding: '0.5rem'}}>🟢 Apple TV+</td>
                <td style={{padding: '0.5rem'}}>Same day (global release)</td>
              </tr>
              <tr style={{borderBottom: '1px solid #222'}}>
                <td style={{padding: '0.5rem'}}>🟡 HBO/Max (via Neon)</td>
                <td style={{padding: '0.5rem'}}>Usually same day, may vary</td>
              </tr>
              <tr style={{borderBottom: '1px solid #222'}}>
                <td style={{padding: '0.5rem'}}>🔴 US Networks (NBC, ABC)</td>
                <td style={{padding: '0.5rem'}}>May be weeks later in NZ</td>
              </tr>
            </tbody>
          </table>

          <p><strong>Tip:</strong> For streaming services like Netflix, Disney+, and Apple TV+, New Zealand often gets content a few hours <em>earlier</em> than the US due to timezone differences! 🇳🇿</p>
        </div>
      )
    },
    {
      id: 'ratings-reviews',
      title: '⭐ Ratings & Reviews',
      keywords: 'ratings reviews score 1-10 share feed writing opinion thoughts',
      content: (
        <div>
          <h4>Share Your Thoughts</h4>
          
          <p><strong>Rating System:</strong></p>
          <ul>
            <li>Rate shows from 1-10 (10 being the best)</li>
            <li>Ratings are optional but required if you want to share a review</li>
            <li>Your personal rating appears on your show cards</li>
          </ul>

          <p><strong>Writing Reviews:</strong></p>
          <ul>
            <li>Add detailed text reviews when adding or editing shows</li>
            <li>Reviews can be as short or long as you like</li>
            <li>Share your honest opinion to help friends discover great shows</li>
          </ul>

          <p><strong>Sharing to Feed:</strong></p>
          <ul>
            <li><strong>Automatic Sharing:</strong> By default, reviews are shared to your feed when you add/edit a show</li>
            <li><strong>Opt-Out:</strong> Uncheck "Share this review to my feed" if you want to keep it private</li>
            <li><strong>Manual Sharing:</strong> Use the "Share Review" button on My Shows page to share later</li>
          </ul>

          <p><strong>Important Notes:</strong></p>
          <ul>
            <li>Only shows with both a rating AND review text can be shared</li>
            <li>Once shared, your review appears in your followers' feeds</li>
            <li>Reviews cannot be edited after sharing (delete and reshare instead)</li>
          </ul>
        </div>
      )
    },
    {
      id: 'statistics',
      title: '📊 Understanding Statistics',
      keywords: 'statistics average ratings friends all users community scores numbers',
      content: (
        <div>
          <h4>Community Ratings Explained</h4>
          
          <p>Baad.tv displays two types of average ratings to help you discover popular shows:</p>

          <p><strong>👥 Friends' Average:</strong></p>
          <ul>
            <li>Shows the average rating from users you follow</li>
            <li>Only includes ratings from people you've followed</li>
            <li>Great for discovering shows your friends love</li>
            <li>Number in parentheses shows how many friends rated it</li>
            <li>Example: ⭐ 9.5/10 (3) = Average 9.5 from 3 friends</li>
          </ul>

          <p><strong>🌎 All Users' Average:</strong></p>
          <ul>
            <li>Shows the average rating from all users on the platform</li>
            <li>Excludes your own rating to give you objective community feedback</li>
            <li>Helps you see how the broader community rates a show</li>
            <li>Number in parentheses shows total user ratings</li>
            <li>Example: ⭐ 8.7/10 (15) = Average 8.7 from 15 users</li>
          </ul>

          <p><strong>Where Statistics Appear:</strong></p>
          <ul>
            <li><strong>My Shows Page:</strong> Detailed statistics in highlighted boxes on each show card</li>
            <li><strong>Dashboard:</strong> Compact statistics badges on recent shows</li>
          </ul>

          <p><strong>Important Notes:</strong></p>
          <ul>
            <li>Statistics only appear when other users have rated the show</li>
            <li>Your own ratings are never included in the averages shown to you</li>
            <li>Statistics update in real-time as more users review shows</li>
          </ul>
        </div>
      )
    },
    {
      id: 'social-feed',
      title: '💬 Social Feed',
      keywords: 'social feed posts reviews like comment heart interact',
      content: (
        <div>
          <h4>Connect with Fellow TV Enthusiasts</h4>
          
          <p><strong>Viewing the Feed:</strong></p>
          <ul>
            <li>Navigate to the "Feed" page from the top navigation</li>
            <li>See reviews from all users you follow, sorted by most recent</li>
            <li>Each post shows: user name, show title, rating, and review text</li>
          </ul>

          <p><strong>Liking Reviews:</strong></p>
          <ul>
            <li>Click the heart icon to like a review</li>
            <li>Liked reviews turn red</li>
            <li>Click again to unlike</li>
            <li>Like count shows how many users liked the review</li>
          </ul>

          <p><strong>Commenting on Reviews:</strong></p>
          <ul>
            <li>Click the comment icon to expand the comment section</li>
            <li>View all existing comments with user names and timestamps</li>
            <li>Type your comment in the text box</li>
            <li>Click "Comment" to post</li>
            <li>Comments appear chronologically</li>
          </ul>

          <p><strong>Empty Feed?</strong></p>
          <ul>
            <li>If your feed is empty, you're not following anyone yet</li>
            <li>Go to the Friends page to search for and follow users</li>
            <li>Once you follow users and they share reviews, they'll appear here</li>
          </ul>
        </div>
      )
    },
    {
      id: 'friends',
      title: '👥 Friends & Following',
      keywords: 'friends following followers users search find network unfollow',
      content: (
        <div>
          <h4>Build Your Network</h4>
          
          <p><strong>Finding Users:</strong></p>
          <ol>
            <li>Go to the "Friends" page</li>
            <li>Use the search box at the top</li>
            <li>Type a username/handle (minimum 4 characters)</li>
            <li>Results appear instantly as you type</li>
          </ol>

          <p><strong>Following Users:</strong></p>
          <ul>
            <li>Click "Follow" next to any user in search results</li>
            <li>They'll immediately appear in your "Following" list</li>
            <li>You'll start seeing their reviews in your feed</li>
            <li>You can follow as many users as you like</li>
          </ul>

          <p><strong>Unfollowing Users:</strong></p>
          <ul>
            <li>Click "Unfollow" next to any user in your Following list</li>
            <li>Their reviews will no longer appear in your feed</li>
            <li>Their ratings won't count toward "Friends' Average" statistics</li>
          </ul>

          <p><strong>Managing Your Network:</strong></p>
          <ul>
            <li><strong>Following Tab:</strong> See all users you follow and their count</li>
            <li><strong>Followers Tab:</strong> See who follows you and their count</li>
            <li><strong>Follow Back:</strong> Quickly follow users who follow you</li>
          </ul>

          <p><strong>Privacy Note:</strong></p>
          <ul>
            <li>Following is one-way (like Twitter/X)</li>
            <li>You don't need mutual following to see someone's reviews</li>
            <li>Users can see who follows them</li>
          </ul>
        </div>
      )
    },
    {
      id: 'recommend',
      title: '💌 Recommending Shows',
      keywords: 'recommend recommendation suggest send friend notify share',
      content: (
        <div>
          <h4>Share Shows with Friends</h4>
          
          <p><strong>Sending Recommendations:</strong></p>
          <ol>
            <li>Go to My Baad.tv (My Shows) page</li>
            <li>Find the show you want to recommend</li>
            <li>Click the recommend icon (paper plane)</li>
            <li>Select friends from your following list</li>
            <li>Add an optional message</li>
            <li>Click "Send Recommendation"</li>
          </ol>

          <p><strong>Receiving Recommendations:</strong></p>
          <ul>
            <li>Check the bell icon for new notifications</li>
            <li>Recommendation notifications show who sent it and which show</li>
            <li>Click "Add to Watchlist" to add the show directly from the notification</li>
            <li>Or dismiss if not interested</li>
          </ul>

          <p>Recommendations help friends discover great shows from people they trust!</p>
        </div>
      )
    },
    {
      id: 'streaming',
      title: '🎬 Streaming Services',
      keywords: 'streaming services netflix hulu disney prime video custom where to watch',
      content: (
        <div>
          <h4>Track Where to Watch</h4>
          
          <p><strong>Preset Services:</strong></p>
          <p>Baad.tv includes popular streaming services like Netflix, Hulu, Disney+, Prime Video, HBO Max, Apple TV+, and more.</p>

          <p><strong>Adding Custom Services:</strong></p>
          <ol>
            <li>When adding/editing a show, click "Custom" toggle</li>
            <li>Enter the service name (e.g., "YouTube", "Criterion Channel")</li>
            <li>The custom service is saved for future use</li>
          </ol>

          <p><strong>Why Track Streaming Services?</strong></p>
          <ul>
            <li>Remember which subscription you need for each show</li>
            <li>Quickly see where to watch shows from your collection</li>
            <li>Manage multiple streaming subscriptions efficiently</li>
          </ul>
        </div>
      )
    },
    {
      id: 'troubleshooting',
      title: '🔧 Troubleshooting',
      keywords: 'troubleshooting problems issues errors fix help bugs login poster search feed statistics modal button episode badge missing',
      content: (
        <div>
          <h4>Common Issues & Solutions</h4>
          
          <p><strong>"Update Show Details" button doesn't work:</strong></p>
          <ul>
            <li>Make sure you've entered a show title first</li>
            <li>Check your internet connection</li>
            <li>Try alternative spellings of the show name</li>
            <li>Use "Custom URL" as a backup option for images</li>
          </ul>

          <p><strong>No details found for a show:</strong></p>
          <ul>
            <li>The show might not be in TMDB database</li>
            <li>Try searching with the full official title</li>
            <li>You can manually enter genre and year information</li>
            <li>Use a custom poster URL for images if needed</li>
          </ul>

          <p><strong>Episode status badge not showing:</strong></p>
          <ul>
            <li>Shows added before the episode tracking feature won't have badges automatically</li>
            <li><strong>To fix:</strong> Edit the show → Click "UPDATE SHOW DETAILS" → Save</li>
            <li>This will fetch the latest episode data from TMDB and enable status badges</li>
            <li>Movies don't have episode badges (only TV shows)</li>
            <li>Some lesser-known shows may not have episode data in TMDB</li>
          </ul>

          <p><strong>Episode badge showing wrong date:</strong></p>
          <ul>
            <li>Air dates are sourced from TMDB and may reflect US broadcast dates</li>
            <li>Streaming services (Netflix, Disney+, Apple TV+) release globally at the same time</li>
            <li>Traditional TV networks (NBC, ABC) may air later in New Zealand</li>
            <li>See the "Episode Status Badges" help section for more details</li>
          </ul>

          <p><strong>Statistics not showing for a show:</strong></p>
          <ul>
            <li>No other users have rated that show yet</li>
            <li>Make sure the show title is spelled exactly the same</li>
            <li>Statistics exclude your own ratings</li>
          </ul>

          <p><strong>Feed is empty:</strong></p>
          <ul>
            <li>You're not following anyone - go to Friends page to follow users</li>
            <li>Users you follow haven't shared any reviews yet</li>
            <li>Ask friends to share their reviews</li>
          </ul>

          <p><strong>Can't see "Update Show" button in modal:</strong></p>
          <ul>
            <li>The modal should be scrollable - try scrolling down</li>
            <li>If using a small screen, zoom out your browser</li>
            <li>The modal is designed to fit within 90% of screen height</li>
          </ul>

          <p><strong>Login issues:</strong></p>
          <ul>
            <li>Make sure you're using a valid Google account</li>
            <li>Enable cookies in your browser</li>
            <li>Try clearing your browser cache</li>
          </ul>

          <p><strong>Still having issues?</strong></p>
          <ul>
            <li>Try refreshing the page</li>
            <li>Clear your browser cache and cookies</li>
            <li>Try a different browser</li>
            <li>Make sure JavaScript is enabled</li>
          </ul>
        </div>
      )
    },
    {
      id: 'sharing',
      title: '🐑 Sharing Baad.tv',
      keywords: 'share invite friends link social whatsapp twitter facebook email referral',
      content: (
        <div>
          <h4>Invite Friends to Join the Flock</h4>
          
          <p><strong>How to Share Baad.tv:</strong></p>
          <ol>
            <li>Click on your profile picture/name in the top-right corner</li>
            <li>Select "Share" from the dropdown menu</li>
            <li>A Share Modal will open with the app URL</li>
            <li>Choose one of the sharing options:
              <ul>
                <li><strong>Copy Link:</strong> Click the "Copy" button to copy the URL to your clipboard</li>
                <li><strong>WhatsApp:</strong> Share directly via WhatsApp</li>
                <li><strong>Twitter:</strong> Tweet about Baad.tv</li>
                <li><strong>Facebook:</strong> Share on Facebook</li>
                <li><strong>Email:</strong> Send an invitation email</li>
              </ul>
            </li>
          </ol>

          <p><strong>Why Invite Friends?</strong></p>
          <ul>
            <li>Build your flock and see what shows they're watching</li>
            <li>Get personalized recommendations from people you trust</li>
            <li>Share and discuss reviews with friends</li>
            <li>Compare ratings and discover new favorites together</li>
          </ul>

          <p><strong>Tip:</strong> The more friends you have on Baad.tv, the more valuable the "Friends' Average" ratings become!</p>
        </div>
      )
    },
    {
      id: 'privacy',
      title: '🔒 Privacy & Safety',
      keywords: 'privacy safety block blocking users unfollow remove unwanted harassment security',
      content: (
        <div>
          <h4>Managing Your Privacy</h4>
          
          <p><strong>Blocking Users:</strong></p>
          <ul>
            <li>Click on a user's profile to view their profile modal</li>
            <li>Click the "Block User" button (red button with ban icon)</li>
            <li>Confirm the action</li>
            <li>Blocked users:
              <ul>
                <li>Cannot follow you</li>
                <li>Are removed from your followers and following lists</li>
                <li>Will not appear in your social feed</li>
                <li>Cannot see your profile or reviews</li>
                <li>Will not appear in search results</li>
              </ul>
            </li>
          </ul>

          <p><strong>Private Reviews:</strong></p>
          <ul>
            <li>When adding or editing a show with a review, uncheck "Share this show, feed your flock"</li>
            <li>The review will be visible only to you on your My Shows page</li>
            <li>Your rating and review will not appear in the social feed</li>
          </ul>

          <p><strong>Unfollowing:</strong></p>
          <ul>
            <li>Go to Friends page → Following tab</li>
            <li>Click "Unfollow" on any user to stop seeing their reviews</li>
            <li>This does not block them - they can still follow you</li>
          </ul>
        </div>
      )
    },
    {
      id: 'security',
      title: '🛡️ Security & Account',
      keywords: 'security account sessions devices logout data export delete privacy limits characters',
      content: (
        <div>
          <h4>Your Account Security</h4>
          <p>Baad.tv takes your security seriously. Here's how to manage your account and data.</p>
          
          <p><strong>Security & Privacy Page:</strong></p>
          <ul>
            <li>Click your profile picture → "Security & Privacy" to access all security settings</li>
            <li>Or click the Help icon → "Security & Privacy"</li>
            <li>This page shows how your data is protected and your privacy controls</li>
          </ul>

          <p><strong>Active Sessions:</strong></p>
          <ul>
            <li>View all devices currently logged into your account on the Security page</li>
            <li>See device type (iPhone, Mac, Windows, etc.) and browser</li>
            <li>Your current session is highlighted with "This device"</li>
            <li>Click the logout icon on any session to revoke it</li>
            <li>Use "Log out all other devices" for a full security reset</li>
          </ul>

          <p><strong>Data Export:</strong></p>
          <ul>
            <li>Download all your data as a .json file from the Security page</li>
            <li>Includes: shows, reviews, follows, followers, notifications, likes, comments</li>
            <li>Limited to 3 exports per hour</li>
          </ul>

          <p><strong>Account Deletion:</strong></p>
          <ul>
            <li>Go to Profile → "Start Deletion Process"</li>
            <li>Choose: <strong>30-day grace period</strong> (cancel anytime) or <strong>immediate deletion</strong></li>
            <li>Type "DELETE MY ACCOUNT" to confirm</li>
            <li>If scheduled, you can cancel from the Security page before 30 days</li>
          </ul>

          <p><strong>Input Limits:</strong></p>
          <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem'}}>
            <tbody>
              <tr>
                <td style={{padding: '0.5rem', borderBottom: '1px solid #333', color: '#b0b0b0'}}>Username</td>
                <td style={{padding: '0.5rem', borderBottom: '1px solid #333', color: '#fff'}}>4-30 characters</td>
              </tr>
              <tr>
                <td style={{padding: '0.5rem', borderBottom: '1px solid #333', color: '#b0b0b0'}}>Display Name</td>
                <td style={{padding: '0.5rem', borderBottom: '1px solid #333', color: '#fff'}}>Up to 100 characters</td>
              </tr>
              <tr>
                <td style={{padding: '0.5rem', borderBottom: '1px solid #333', color: '#b0b0b0'}}>Reviews</td>
                <td style={{padding: '0.5rem', borderBottom: '1px solid #333', color: '#fff'}}>Up to 5,000 characters</td>
              </tr>
              <tr>
                <td style={{padding: '0.5rem', borderBottom: '1px solid #333', color: '#b0b0b0'}}>Comments</td>
                <td style={{padding: '0.5rem', borderBottom: '1px solid #333', color: '#fff'}}>Up to 1,000 characters</td>
              </tr>
              <tr>
                <td style={{padding: '0.5rem', color: '#b0b0b0'}}>Ratings</td>
                <td style={{padding: '0.5rem', color: '#fff'}}>1-10 only</td>
              </tr>
            </tbody>
          </table>
        </div>
      )
    },
    {
      id: 'tips',
      title: '💡 Pro Tips',
      keywords: 'tips tricks pro advice best practices filters engage dashboard share invite blocking',
      content: (
        <div>
          <h4>Get the Most Out of Baad.tv</h4>
          
          <ul>
            <li><strong>Use Filters:</strong> On My Shows page, use the icon filters (All Shows, Watching, Waiting, Completed, Want to Watch) to quickly view specific categories</li>
            <li><strong>Search Accurately:</strong> When updating show details, use exact show titles for best results</li>
            <li><strong>Review Regularly:</strong> Share reviews as you watch to build an engaging feed for your followers</li>
            <li><strong>Follow Strategically:</strong> Follow users with similar taste to discover great shows</li>
            <li><strong>Check Statistics:</strong> Use Friends' Average to find shows your circle loves</li>
            <li><strong>Custom Services:</strong> Add niche streaming services you use</li>
            <li><strong>Status Updates:</strong> Move shows between statuses as you progress</li>
            <li><strong>Engage:</strong> Like and comment on friends' reviews to stay connected</li>
            <li><strong>Dashboard Quick View:</strong> Check your dashboard regularly for stats and recent activity</li>
            <li><strong>Private Reviews:</strong> Uncheck "Share to feed" if you want to keep personal notes</li>
            <li><strong>Invite Friends:</strong> Use the Share feature from your profile menu to invite friends and build your flock</li>
            <li><strong>Sorting Shows:</strong> Use the sort dropdown on My Shows page to organize by rating, date, title, or release date</li>
          </ul>
        </div>
      )
    }
  ];

  // Filter sections based on search query
  const filteredSections = sections.filter(section => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const titleMatch = section.title.toLowerCase().includes(query);
    const keywordsMatch = section.keywords.toLowerCase().includes(query);
    
    return titleMatch || keywordsMatch;
  });

  return (
    <div className="help-container">
      <Navigation user={user} setUser={setUser} currentPage="help" />
      
      <div className="help-content">
        <div className="help-header">
          <h1 data-testid="help-title">Help & User Guide</h1>
          <p>Everything you need to know about using Baad.tv</p>
        </div>

        <div className="search-box">
          <Search className="search-icon" />
          <Input
            type="text"
            placeholder="Search help topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            data-testid="help-search-input"
          />
          {searchQuery && (
            <span className="search-results-count">
              {filteredSections.length} result{filteredSections.length !== 1 ? 's' : ''} found
            </span>
          )}
        </div>

        {filteredSections.length === 0 && (
          <div className="no-results">
            <p>No help topics found for "{searchQuery}"</p>
            <p className="no-results-hint">Try different keywords or browse all sections</p>
          </div>
        )}

        <div className="help-sections">
          {filteredSections.map((section) => (
            <Card key={section.id} className="help-section-card">
              <CardHeader
                className="section-header"
                onClick={() => toggleSection(section.id)}
                data-testid={`help-section-${section.id}`}
              >
                <div className="section-title-row">
                  <CardTitle>{section.title}</CardTitle>
                  {expandedSection === section.id ? (
                    <ChevronUp className="chevron-icon" />
                  ) : (
                    <ChevronDown className="chevron-icon" />
                  )}
                </div>
              </CardHeader>
              {expandedSection === section.id && (
                <CardContent className="section-content">
                  {section.content}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </div>

      <style jsx>{`
        .help-container {
          min-height: 100vh;
          background: #121212;
        }

        .help-content {
          max-width: 1000px;
          margin: 0 auto;
          padding: 2rem;
        }

        .help-header {
          margin-bottom: 2.5rem;
        }

        .help-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .help-header p {
          color: #b0b0b0;
          font-size: 1.1rem;
        }

        .search-box {
          position: relative;
          margin-bottom: 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 0.75rem;
          color: #b0b0b0;
          width: 20px;
          height: 20px;
        }

        .search-input {
          padding-left: 3rem;
          height: 3rem;
          font-size: 1rem;
          border-radius: 8px;
          border: 2px solid #e9ecef;
          transition: border-color 0.2s;
        }

        .search-input:focus {
          border-color: #667eea;
        }

        .search-results-count {
          font-size: 0.9rem;
          color: #b0b0b0;
          font-weight: 500;
        }

        .no-results {
          text-align: center;
          padding: 3rem;
          color: #b0b0b0;
        }

        .no-results p {
          margin-bottom: 0.5rem;
          font-size: 1.1rem;
        }

        .no-results-hint {
          font-size: 0.95rem;
          color: #999;
        }

        .help-sections {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .help-section-card {
          border-radius: 8px;
          border: none;
          border: 1px solid #333333;
          overflow: hidden;
        }

        .section-header {
          cursor: pointer;
          transition: background 0.2s;
          padding: 1.5rem;
        }

        .section-header:hover {
          background: #121212;
        }

        .section-title-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .chevron-icon {
          width: 24px;
          height: 24px;
          color: #667eea;
        }

        .section-content {
          padding: 0 1.5rem 1.5rem 1.5rem;
          color: #b0b0b0;
          line-height: 1.7;
        }

        .section-content h4 {
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1rem;
          margin-top: 0;
        }

        .section-content p {
          margin-bottom: 1rem;
        }

        .section-content strong {
          color: #ffffff;
          font-weight: 600;
        }

        .section-content ul,
        .section-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }

        .section-content li {
          margin-bottom: 0.5rem;
        }

        .section-content ul ul,
        .section-content ol ul {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }

        @media (max-width: 768px) {
          .help-content {
            padding: 1rem;
            padding-bottom: 100px;
          }

          .help-header {
            margin-bottom: 1.5rem;
          }

          .help-header h1 {
            font-size: 1.75rem;
          }

          .help-header p {
            font-size: 1rem;
          }

          .search-box {
            margin-bottom: 1.5rem;
          }

          .search-input {
            height: 2.75rem;
            font-size: 0.95rem;
            padding-left: 2.75rem;
          }

          .search-icon {
            left: 0.875rem;
            top: 0.875rem;
            width: 18px;
            height: 18px;
          }

          .help-sections {
            gap: 0.75rem;
          }

          .section-header {
            padding: 1rem;
          }

          .section-title-row {
            font-size: 0.95rem;
          }

          .chevron-icon {
            width: 20px;
            height: 20px;
          }

          .section-content {
            padding: 0 1rem 1rem 1rem;
            font-size: 0.95rem;
          }

          .section-content h4 {
            font-size: 1.1rem;
          }

          .section-content ul,
          .section-content ol {
            padding-left: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Help;